import { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

const KIND_CLASS = {
  info: "bg-blue-500", success: "bg-emerald-500",
  warning: "bg-amber-500", danger: "bg-red-500",
};

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter(n => !n.read).length;

  const markRead = async (n, opts = {}) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (opts.navigate && n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-9 h-9 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center relative" data-testid="notifications-btn">
          <Bell className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand-aqua text-white text-[10px] font-medium flex items-center justify-center num">{unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 max-h-96 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
          <div className="text-sm font-heading font-semibold text-slate-800 dark:text-slate-100">Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-brand-aqua-hover hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 && <div className="text-xs text-slate-400 text-center py-8">All caught up.</div>}
          {items.map(n => (
            <button key={n.id} onClick={() => markRead(n, { navigate: true })}
                    className={`w-full text-left px-3 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${n.read ? "opacity-60" : ""}`}
                    data-testid={`notif-${n.id}`}>
              <div className="flex items-start gap-2">
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${KIND_CLASS[n.kind] || "bg-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{n.body}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(n.at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
